import {
  liquidGlassPillShadowSx,
  liquidGlassPressTransition,
  liquidGlassSurfaceFillSx,
  LIQUID_GLASS_CIRCLE_PRESS_SCALE,
  MainActionButton,
  type MainActionButtonProps,
  MINI_BUTTON_ACTION_SIZE,
  MINI_BUTTON_DOCK_GAP,
  miniButtonDockBottomInset,
  SecondaryActionButton,
  type SecondaryActionButtonProps,
  useLiquidGlassPress,
} from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { motion } from 'framer-motion';
import { FC, useEffect, useRef } from 'react';
import { CommonIcon } from './CommonIcon';
import { LiquidGlassCircleButton } from './LiquidGlassCircleButton';

const bottomUiInset = `calc(${MINI_BUTTON_DOCK_GAP}px + ${miniButtonDockBottomInset})`;

export interface SearchableButtonDockProps {
  main?: MainActionButtonProps;
  secondary?: SecondaryActionButtonProps;
  searchOpen: boolean;
  searchQuery: string;
  searchPlaceholder: string;
  searchAriaLabel: string;
  closeSearchAriaLabel: string;
  onSearchQueryChange: (value: string) => void;
  onOpenSearch: () => void;
  onCloseSearch: () => void;
}

export const SearchableButtonDock: FC<SearchableButtonDockProps> = ({
  main,
  secondary,
  searchOpen,
  searchQuery,
  searchPlaceholder,
  searchAriaLabel,
  closeSearchAriaLabel,
  onSearchQueryChange,
  onOpenSearch,
  onCloseSearch,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelPress = useLiquidGlassPress({ onActivate: onCloseSearch });
  const mainVisible = !!main;
  const secondaryVisible = !!secondary;
  const bothButtonsVisible = mainVisible && secondaryVisible;

  useEffect(() => {
    if (searchOpen) inputRef.current?.focus();
  }, [searchOpen]);

  return (
    <Box
      sx={{
        flexShrink: 0,
        width: '100%',
        boxSizing: 'border-box',
        pb: bottomUiInset,
        pt: `${MINI_BUTTON_DOCK_GAP}px`,
        pointerEvents: 'none',
        '& button, & [role="button"]': { pointerEvents: 'auto' },
      }}
    >
      {searchOpen ? (
        <Box sx={{ px: `${MINI_BUTTON_DOCK_GAP}px`, pb: 1, pointerEvents: 'auto' }}>
          <Box
            sx={{
              position: 'relative',
              height: MINI_BUTTON_ACTION_SIZE,
              borderRadius: '999px',
              overflow: 'visible',
              ...liquidGlassPillShadowSx,
            }}
          >
            <Box
              sx={{
                height: '100%',
                borderRadius: 'inherit',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                ...liquidGlassSurfaceFillSx,
              }}
              onPointerDown={event => {
                if (!(event.target as HTMLElement).closest('button')) inputRef.current?.focus();
              }}
            >
              <CommonIcon
                icon="search"
                type="filled"
                size={18}
                color="var(--tg-theme-text-color)"
                style={{ flexShrink: 0, marginLeft: 14, marginRight: 8 }}
              />
              <Box
                component="input"
                ref={inputRef}
                type="text"
                inputMode="search"
                enterKeyHint="search"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                placeholder={searchPlaceholder}
                aria-label={searchAriaLabel}
                value={searchQuery}
                onChange={e => onSearchQueryChange(e.target.value)}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height: MINI_BUTTON_ACTION_SIZE,
                  border: 'none',
                  outline: 'none',
                  bgcolor: 'transparent',
                  color: 'var(--tg-theme-text-color)',
                  fontSize: 16,
                  lineHeight: `${MINI_BUTTON_ACTION_SIZE}px`,
                  px: 0,
                  py: 0,
                  margin: 0,
                  boxSizing: 'border-box',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  '&::placeholder': { color: 'var(--tg-theme-hint-color)', opacity: 1 },
                }}
              />
              <Box
                component={motion.div}
                initial={false}
                animate={{ scale: cancelPress.pressed ? LIQUID_GLASS_CIRCLE_PRESS_SCALE : 1 }}
                transition={liquidGlassPressTransition}
                sx={{ flexShrink: 0, mr: '4px' }}
              >
                <Box
                  component="button"
                  type="button"
                  aria-label={closeSearchAriaLabel}
                  onPointerDown={cancelPress.onPointerDown}
                  onPointerMove={cancelPress.onPointerMove}
                  onPointerUp={cancelPress.onPointerUp}
                  onPointerCancel={cancelPress.onPointerCancel}
                  onClick={cancelPress.onClick}
                  sx={{
                    width: MINI_BUTTON_ACTION_SIZE - 8,
                    height: MINI_BUTTON_ACTION_SIZE - 8,
                    border: 'none',
                    bgcolor: 'transparent',
                    color: 'var(--tg-theme-text-color)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    WebkitTapHighlightColor: 'transparent',
                    outline: 'none',
                    '&:focus-visible': { outline: '2px solid var(--tg-theme-link-color)', outlineOffset: 2 },
                  }}
                >
                  <CommonIcon icon="close" type="filled" size={20} color="var(--tg-theme-text-color)" />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : null}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: bothButtonsVisible ? 'space-between' : 'flex-end',
          gap: `${MINI_BUTTON_DOCK_GAP}px`,
          width: '100%',
          boxSizing: 'border-box',
          px: `${MINI_BUTTON_DOCK_GAP}px`,
          minHeight: MINI_BUTTON_ACTION_SIZE,
        }}
      >
        {secondary && <SecondaryActionButton {...secondary} />}
        <Stack direction="row" alignItems="center" gap={`${MINI_BUTTON_DOCK_GAP}px`}>
          {!searchOpen && (
            <LiquidGlassCircleButton
              icon="search"
              ariaLabel={searchAriaLabel}
              onClick={onOpenSearch}
              size={MINI_BUTTON_ACTION_SIZE}
              iconSize={24}
            />
          )}
          {main && <MainActionButton {...main} $fullWidth={!bothButtonsVisible && !secondaryVisible} />}
        </Stack>
      </Box>
    </Box>
  );
};
